import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ModalRow } from "components/Rows";
import {
  toLocaleStringWithCurrency,
  normolizeCurrenciesDecimals,
  convertCurrencyValue,
} from "helpers/funcs";
import { InfoRows } from "components/Modals/styled";
import type {
  AvailableTradeOptions,
  CurrencyInterface,
  FeesInterface,
  PricesInterface,
} from "helpers/types";

interface TradeInfoRowsProps {
  currencyValue: string;
  currency: AvailableTradeOptions | null;
  productCurrency?: string | null;
  fees?: FeesInterface | null;
  cost: number;
  isFeesInUsdt?: boolean;
  prices?: PricesInterface | null;
  usdCost?: number;
  currencies: CurrencyInterface[] | null;
  isCrypto?: boolean;
  userCard?: boolean;
  isWithdraw?: boolean;
  isTopUp?: boolean;
  currentFee?: string;
  isSuccess?: boolean;
  eurValue?: string;
  fixedEURFee?: number;
  fixedFeeInCrypto?: number;
  comission?: number;
  cardWithDiscountGift?: boolean;
  cardIsFree?: boolean;
}

const BuyInfoRows: FC<TradeInfoRowsProps> = ({
  currencyValue,
  currency,
  productCurrency,
  fees,
  cost,
  isFeesInUsdt,
  prices,
  usdCost,
  currencies,
  isCrypto,
  userCard,
  isWithdraw,
  isTopUp,
  currentFee,
  isSuccess,
  eurValue,
  fixedEURFee,
  fixedFeeInCrypto,
  comission,
  cardWithDiscountGift,
  cardIsFree,
}) => {
  const { t } = useTranslation();

  return (
    <InfoRows>
      <ModalRow
        keyText={t("RATE")}
        value={`${cost > 0 ? "≈ " : ""}${toLocaleStringWithCurrency(
          cost,
          productCurrency || "USD"
        )}`}
      />
      {cardWithDiscountGift && (
        <>
          <ModalRow
            keyText={t("ORIGINAL_PRICE")}
            value={`${normolizeCurrenciesDecimals(
              (Number(currencyValue) || 0) / Number(cost),
              currency || "USD",
              currencies,
              "forView"
            )} ${currency || ""}`}
          />
          <ModalRow
            keyText={t("DISCOUNT")}
            value={`${normolizeCurrenciesDecimals(
              (Number(currencyValue) || 0) / Number(cost) -
                ((Number(currencyValue) || 0) / Number(cost)) * 0.4,
              currency || "USD",
              currencies,
              "forView"
            )} ${currency || ""}`}
          />
        </>
      )}
      {cardIsFree && (
        <>
          <ModalRow
            keyText={t("ORIGINAL_PRICE")}
            value={`${normolizeCurrenciesDecimals(
              (Number(currencyValue) || 0) / Number(cost),
              currency || "USD",
              currencies,
              "forView"
            )} ${currency || ""}`}
          />
          <ModalRow
            keyText={t("DISCOUNT")}
            value={`${normolizeCurrenciesDecimals(
              Number(currencyValue) || 0,
              currency || "USD",
              currencies,
              "forView"
            )} ${currency || ""}`}
          />
        </>
      )}
      {!currentFee && (
        <ModalRow
          keyText={t("YOU_SPEND")}
          value={`${normolizeCurrenciesDecimals(
            userCard
              ? Number(currencyValue) || 0
              : cardWithDiscountGift
              ? ((Number(currencyValue) || 0) / Number(cost)) * 0.4
              : cardIsFree
              ? 0
              : (Number(currencyValue) || 0) / Number(cost),
            isWithdraw ? productCurrency || "USD" : currency || "USD",
            currencies,
            "forView"
          )} ${isWithdraw ? productCurrency || "" : currency || ""}`}
        />
      )}
      {isSuccess && (
        <ModalRow
          keyText={t("YOU_SPENT")}
          value={`${normolizeCurrenciesDecimals(
            userCard
              ? eurValue
                ? eurValue
                : Number(currencyValue) || 0
              : (Number(currencyValue) || 0) / Number(cost),
            isWithdraw ? productCurrency || "USD" : currency || "USD",
            currencies,
            "forView"
          )} ${isWithdraw ? productCurrency || "" : currency || ""}`}
        />
      )}

      {!isCrypto && fees && (
        <ModalRow
          keyText={t("FEES")}
          value={`${normolizeCurrenciesDecimals(
            isTopUp || isWithdraw
              ? (Number(currencyValue) || 0) *
                  (fees ? Number(fees.value.feePercent) : 0 || 0)
              : ((Number(currencyValue) || 0) *
                  (fees ? Number(fees.value.feePercent) : 0 || 0)) /
                  Number(cost),
            isWithdraw ? productCurrency || "USD" : currency || "USD",
            currencies,
            "forView"
          )} ${isWithdraw ? productCurrency || "USD" : currency || "USD"}`}
        />
      )}
      {currentFee && (
        <ModalRow
          keyText={t("INCLUDING_FEES")}
          value={`${normolizeCurrenciesDecimals(
            currentFee,
            isWithdraw ? productCurrency || "USD" : currency || "USD",
            currencies,
            "forView"
          )} ${isWithdraw ? productCurrency || "USD" : currency || "USD"}`}
        />
      )}

      {!isWithdraw && !isTopUp && fees && (
        <ModalRow
          keyText={t("FIXED_FEE")}
          value={`${normolizeCurrenciesDecimals(
            isFeesInUsdt && currency && prices
              ? convertCurrencyValue(
                  "USDT",
                  isWithdraw ? productCurrency || "USD" : currency || "USD",
                  Number(fees.value.fixedUSDTFee) || 0,
                  prices
                )
              : usdCost
              ? (Number(fees.value.fixedUSDTFee) || 0) / Number(usdCost)
              : 0,
            isWithdraw ? productCurrency || "USD" : currency || "USD",
            currencies,
            "forView"
          )} ${isWithdraw ? productCurrency || "USD" : currency || "USD"}`}
        />
      )}

      {userCard && typeof comission === "number" && (
        <ModalRow
          keyText={t("PROVIDER_COMMISSION")}
          value={`${normolizeCurrenciesDecimals(
            comission,
            "EUR",
            currencies,
            "forView"
          )} EUR`}
        />
      )}

      {userCard &&
        isWithdraw &&
        typeof fixedEURFee === "number" &&
        fixedEURFee > 0 && (
          <ModalRow
            keyText={t("FIXED_FEE")}
            value={`${normolizeCurrenciesDecimals(
              fixedEURFee,
              "EUR",
              currencies,
              "forView"
            )} EUR`}
          />
        )}

      {userCard &&
        isTopUp &&
        typeof fixedFeeInCrypto === "number" &&
        fixedFeeInCrypto > 0 && (
          <ModalRow
            keyText={t("FIXED_FEE")}
            value={`${normolizeCurrenciesDecimals(
              fixedFeeInCrypto,
              currency || "USDT",
              currencies,
              "forView"
            )} ${currency}`}
          />
        )}
    </InfoRows>
  );
};

export default BuyInfoRows;
